import { animate, type MotionValue, motionValue } from 'motion/react';
import { useCallback, useEffect, useRef } from 'react';

import {
  type BirdGameEngine,
  type BirdGameMotion,
  createBirdGameEngine,
  playBottomBound,
  playTopBound,
} from './bird-game-engine';
import {
  BIRD_SIZE,
  BIRD_X_RATIO,
  IDLE_HOVER_AMPLITUDE,
  IDLE_HOVER_DURATION_S,
  MAX_PIPE_COUNT,
} from './bird-game.constants';
import { birdGameStore, type Point } from './bird-game.store';

const WING_FLAP_KEYFRAMES = [0, -55, -5, -40, 0];
const WING_FLAP_DURATION_S = 0.5;

const createMotionChannels = (): BirdGameMotion => ({
  birdX: motionValue(0),
  birdY: motionValue(0),
  birdRotation: motionValue(0),
  pipes: Array.from({ length: MAX_PIPE_COUNT }, () => ({
    x: motionValue(0),
    topHeight: motionValue(0),
    bottomTop: motionValue(0),
    bottomHeight: motionValue(0),
  })),
});

export type BirdGameController = ReturnType<typeof useBirdGame>;

export function useBirdGame() {
  const motionRef = useRef<BirdGameMotion | null>(null);
  motionRef.current ??= createMotionChannels();
  const motion = motionRef.current;

  const wingAngleRef = useRef<MotionValue<number> | null>(null);
  wingAngleRef.current ??= motionValue(0);
  const wingAngle = wingAngleRef.current;

  const engineRef = useRef<BirdGameEngine | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const flapWings = useCallback(() => {
    animate(wingAngle, WING_FLAP_KEYFRAMES, {
      duration: WING_FLAP_DURATION_S,
      ease: 'easeOut',
    });
  }, [wingAngle]);

  const startIdleHover = useCallback(() => {
    const center = motion.birdY.get();
    animate(
      motion.birdY,
      [center - IDLE_HOVER_AMPLITUDE, center + IDLE_HOVER_AMPLITUDE],
      {
        duration: IDLE_HOVER_DURATION_S,
        ease: 'easeInOut',
        repeat: Infinity,
        repeatType: 'mirror',
      }
    );
  }, [motion.birdY]);

  useEffect(() => {
    const engine = createBirdGameEngine(motion, {
      onScore: (score) => birdGameStore.setScore(score),
      onGameOver: (score) => birdGameStore.endRun(score),
    });
    engineRef.current = engine;
    return () => {
      engine.stop();
      engineRef.current = null;
    };
  }, [motion]);

  const isSummoning = birdGameStore.phase === 'flying-in';
  useEffect(() => {
    if (!isSummoning) return;
    motion.birdY.stop();
    engineRef.current?.resetToSpawn();
  }, [isSummoning, motion.birdY]);

  const attachStage = useCallback(
    (element: HTMLDivElement | null) => {
      stageRef.current = element;
      if (!element) return;
      const observer = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        const isIdle = birdGameStore.status === 'idle';
        if (isIdle) motion.birdY.stop();
        engineRef.current?.setBounds(width, height);
        if (isIdle) startIdleHover();
      });
      observer.observe(element);
      return () => observer.disconnect();
    },
    [motion.birdY, startIdleHover]
  );

  const flap = useCallback(() => {
    engineRef.current?.flap();
    flapWings();
  }, [flapWings]);

  const startRun = useCallback(() => {
    motion.birdY.stop();
    if (!engineRef.current?.startRun()) return;
    birdGameStore.beginRun();
    flapWings();
  }, [motion.birdY, flapWings]);

  const handleTap = useCallback(() => {
    const { status, phase } = birdGameStore;
    if (phase !== 'open') return;
    if (status === 'playing') {
      flap();
      return;
    }
    if (status === 'paused') {
      birdGameStore.resumeRun();
      engineRef.current?.resume();
      return;
    }
    startRun();
  }, [flap, startRun]);

  const pause = useCallback(() => {
    if (birdGameStore.status !== 'playing') return;
    engineRef.current?.pause();
    birdGameStore.pauseRun();
  }, []);

  const leaveGame = useCallback(() => {
    const stage = stageRef.current?.getBoundingClientRect();
    const returnFrom: Point | null = stage
      ? {
          x: stage.left + motion.birdX.get(),
          y: stage.top + motion.birdY.get() + BIRD_SIZE / 2,
        }
      : null;
    motion.birdY.stop();
    engineRef.current?.stop();
    birdGameStore.leave(returnFrom);
  }, [motion.birdX, motion.birdY]);

  const getSpawnPoint = useCallback((): Point | null => {
    const stage = stageRef.current?.getBoundingClientRect();
    if (!stage || stage.width === 0) return null;
    return {
      x: stage.left + stage.width * BIRD_X_RATIO,
      y: stage.top + (playTopBound() + playBottomBound(stage.height)) / 2,
    };
  }, []);

  const isPlayable = birdGameStore.phase === 'open';

  useEffect(() => {
    if (!isPlayable) return;
    stageRef.current?.focus({ preventScroll: true });
  }, [isPlayable]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        leaveGame();
        return;
      }
      if (event.key === ' ' || event.key === 'ArrowUp') {
        event.preventDefault();
        handleTap();
      }
    },
    [leaveGame, handleTap]
  );

  useEffect(() => {
    window.addEventListener('blur', pause);
    return () => window.removeEventListener('blur', pause);
  }, [pause]);

  return {
    motion,
    wingAngle,
    attachStage,
    handleTap,
    handleKeyDown,
    pause,
    leaveGame,
    getSpawnPoint,
    startIdleHover,
  };
}
